import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlus,
  faEdit,
  faTrash,
  faSearch,
  faEye,
  faImages,
  faLink,
  faExclamationTriangle,
} from "@fortawesome/free-solid-svg-icons";
import { getProjets, deleteProjet } from "../../services/api";
import { Projet } from "../../types/types";
import { toast } from "react-toastify";
import { useLanguage } from "../../context/LanguageContext";
import { getTranslation } from "../../utils/translations";
import AdminLayout from "./AdminLayout";
import { resolveMediaUrl } from "../../utils/media";

const ProjetsList: React.FC = () => {
  const [projets, setProjets] = useState<Projet[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const { language } = useLanguage();

  useEffect(() => {
    fetchProjets();
  }, []);

  const fetchProjets = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await getProjets();
      console.log("API Response:", response.data); // Debug log

      // Ensure the response data matches our Projet interface
      const projetsData = response.data.map((projet: any) => ({
        id: projet.id,
        titrearabe: projet.titrearabe || "",
        titrefrancais: projet.titrefrancais || "",
        descarabe: projet.descarabe || "",
        descfrancais: projet.descfrancais || "",
        lien: projet.lien || "",
        images: Array.isArray(projet.images)
          ? projet.images.map((img: any) => ({
              image: img.image || "",
            }))
          : [],
      }));

      setProjets(projetsData);
    } catch (error: any) {
      console.error("Error fetching projects:", error);
      setError(error.response?.data?.message || "حدث خطأ أثناء جلب المشاريع");
      toast.error("حدث خطأ أثناء جلب المشاريع");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id: number) => {
    if (window.confirm("هل أنت متأكد من حذف هذا المشروع؟")) {
      try {
        await deleteProjet(id);
        toast.success("تم حذف المشروع بنجاح");
        fetchProjets();
      } catch (error) {
        console.error("Error deleting project:", error);
        toast.error("حدث خطأ أثناء حذف المشروع");
      }
    }
  };

  const getFirstImage = (images: Array<{ image: string }> = []) => {
    if (!images || images.length === 0) return null;

    try {
      const firstImage = images[0];
      return firstImage.image;
    } catch (err) {
      console.error("Error parsing image:", err);
      return null;
    }
  };

  const filteredProjets = projets.filter(
    (p) =>
      p.titrearabe?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.titrefrancais?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.descarabe?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      p.descfrancais?.toLowerCase().includes(searchTerm.toLowerCase()),
  );

  if (error) {
    return (
      <AdminLayout>
        <div className="text-center py-12">
          <div className="bg-red-50 border border-red-200 rounded-md p-6 max-w-md mx-auto">
            <FontAwesomeIcon
              icon={faExclamationTriangle}
              className="text-red-500 text-4xl mb-4"
            />
            <h2 className="text-xl font-bold text-red-700 mb-2">
              خطأ في {getTranslation("download", language)} البيانات
            </h2>
            <p className="text-red-600 mb-4">{error}</p>
            <button
              onClick={fetchProjets}
              className="bg-mauritania-green text-white py-2 px-4 rounded hover:bg-mauritania-green-dark transition duration-300"
            >
              إعادة المحاولة
            </button>
          </div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
        <h1 className="text-3xl font-bold text-mauritania-green-dark mb-4 md:mb-0">
          {getTranslation("adminProjectsTitle", language)}
        </h1>
        <Link
          to="/admin/projets/new"
          className="bg-mauritania-green text-white py-2 px-4 rounded hover:bg-mauritania-green-dark transition duration-300"
        >
          <FontAwesomeIcon icon={faPlus} className="ml-2" />
          {getTranslation("addProject", language)}
        </Link>
      </div>

      <div className="bg-white rounded-lg shadow-md p-6 mb-8">
        <div className="relative">
          <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
            <FontAwesomeIcon icon={faSearch} className="text-gray-400" />
          </div>
          <input
            type="text"
            className="w-full pr-10 pl-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-mauritania-green focus:border-transparent"
            placeholder="ابحث في المشاريع..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="inline-block animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-mauritania-green"></div>
          <p className="mt-2 text-gray-600">
            {getTranslation("loadingData", language)}
          </p>
        </div>
      ) : filteredProjets.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg shadow-md">
          <p className="text-gray-600">لا توجد مشاريع متاحة</p>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {filteredProjets.map((projet) => {
            const firstImage = getFirstImage(projet.images);

            return (
              <div
                key={projet.id}
                className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow duration-300"
              >
                {/* Project Image */}
                <div className="h-48 bg-gray-200 relative">
                  {firstImage ? (
                    <img
                      src={resolveMediaUrl(firstImage)}
                      alt={projet.titrearabe || "Project"}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        e.currentTarget.style.display = "none";
                        const parent = e.currentTarget.parentElement;
                        if (parent) {
                          parent.innerHTML = `
                            <div class="w-full h-full flex items-center justify-center bg-gray-100">
                              <i class="fas fa-images text-gray-400 text-4xl"></i>
                            </div>
                          `;
                        }
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-100">
                      <FontAwesomeIcon
                        icon={faImages}
                        className="text-gray-400 text-4xl"
                      />
                    </div>
                  )}
                  <div className="absolute top-2 right-2 bg-black bg-opacity-50 text-white text-xs px-2 py-1 rounded">
                    <FontAwesomeIcon icon={faImages} className="ml-1" />
                    {projet.images?.length || 0} صورة
                  </div>
                </div>

                {/* Project Info */}
                <div className="p-4">
                  <h3 className="font-bold text-lg text-gray-900 mb-2">
                    {projet.titrearabe || "بدون عنوان"}
                  </h3>
                  <p className="text-sm text-gray-600 mb-1">
                    {projet.titrefrancais || "Sans titre"}
                  </p>
                  <div
                    className="text-sm text-gray-500 mb-3 line-clamp-2"
                    dangerouslySetInnerHTML={{
                      __html:
                        (projet.descarabe || "").substring(0, 100) +
                        ((projet.descarabe || "").length > 100 ? "..." : ""),
                    }}
                  />

                  {/* Link */}
                  {projet.lien && (
                    <div className="mb-3">
                      <a
                        href={projet.lien}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:text-blue-800 text-sm flex items-center"
                      >
                        <FontAwesomeIcon
                          icon={faLink}
                          className="ml-1 text-xs"
                        />
                        {projet.lien.length > 30
                          ? projet.lien.substring(0, 30) + "..."
                          : projet.lien}
                      </a>
                    </div>
                  )}

                  {/* Actions */}
                  <div className="flex justify-between items-center pt-3 border-t border-gray-200">
                    <div className="flex space-x-2">
                      <Link
                        to={`/admin/projets/edit/${projet.id}`}
                        className="text-mauritania-green hover:text-mauritania-green-dark"
                        title="تعديل"
                      >
                        <FontAwesomeIcon icon={faEdit} />
                      </Link>
                      <button
                        onClick={() => handleDelete(projet.id!)}
                        className="text-red-600 hover:text-red-900"
                        title="حذف"
                      >
                        <FontAwesomeIcon icon={faTrash} />
                      </button>
                    </div>
                    <Link
                      to={`/admin/projets/view/${projet.id}`}
                      className="text-gray-600 hover:text-gray-900 text-sm"
                    >
                      <FontAwesomeIcon icon={faEye} className="ml-1" />
                      {getTranslation("preview", language)}
                    </Link>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </AdminLayout>
  );
};

export default ProjetsList;
